/**
 * Panel — Dashboard-Abschnitt: Overline + Titel links, Meta/Aktion rechts.
 * DESIGN.md: weiße Fläche, 1px Slate-Linie, radius lg (12px), kein Deko-Schatten.
 * Mobil: `metaWrap` setzt Meta/Aktion unter den Titel statt ihn abzuschneiden.
 */
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

export function Panel({ overline, title, meta, metaWrap = false, children, className, bodyClassName }) {
  return (
    <section className={cn('bg-white border border-slate-200 rounded-xl flex flex-col min-w-0', className)}>
      <header className={cn('flex items-start justify-between gap-x-3 gap-y-2 px-4 sm:px-5 pt-4 pb-3', metaWrap && 'flex-wrap')}>
        <div className={cn('min-w-0', metaWrap && 'basis-full sm:basis-auto sm:flex-1')}>
          {overline && (
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">{overline}</p>
          )}
          <h2 className="font-display text-lg font-bold tracking-tight text-slate-900 truncate">{title}</h2>
        </div>
        {meta && <div className={cn('shrink-0 text-right', metaWrap && 'basis-auto')}>{meta}</div>}
      </header>
      <div className={cn('px-4 sm:px-5 pb-5 flex-1', bodyClassName)}>{children}</div>
    </section>
  );
}

/** Leerer Zustand: kurz, sagt was fehlt und wie es entsteht. */
export function PanelEmpty({ icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-8 gap-2">
      {icon && <span className="text-slate-300">{icon}</span>}
      <p className="text-sm font-medium text-slate-600">{title}</p>
      {hint && <p className="text-xs text-slate-400 max-w-[32ch]">{hint}</p>}
    </div>
  );
}

/** Skelett-Zeilen in Listenhöhe (statt Spinner). */
export function PanelSkeleton({ rows = 5 }) {
  return (
    <div className="space-y-3 pt-1" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-6" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}
